import type {
  BatchRequest,
  BatchSaveParams,
  CloseParams,
  CloseRequest,
  CloseResponse,
  ContentParams,
  FollowRequest,
  FollowUpParams,
  HistoryRequest,
  HistoryResponse,
  PageParams,
  PageResponse,
  PreviewParams,
  PreviewRequest,
  PreviewResponse,
  SaveParams,
  StatsParams,
  StatsRequest,
  WeeklyItem,
  WeeklyResponse,
} from './type/account-status-monitor'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { EXPORT_TASK_BIZ_CODE, exportTaskApi } from '#/api/modules/common'
import { requestClient } from '#/api/request'

import { mapFollowHistoryResponse } from './account-status-monitor-follow-history-mapper'
import {
  buildQueryRecordPageRequest,
  mapQueryRecordPageResponse,
} from './account-status-monitor-record-mapper'

export {
  buildQueryRecordPageRequest,
  mapQueryRecordPageResponse,
} from './account-status-monitor-record-mapper'

function normalizeChangeLogId(recordId: number | string) {
  const changeLogId = Number(recordId)
  if (!Number.isInteger(changeLogId) || changeLogId < 1) {
    throw new Error(`Invalid account status monitor record id: ${recordId}`)
  }
  return changeLogId
}

function buildFollowRequest(params: SaveParams): FollowRequest {
  return {
    changeLogId: normalizeChangeLogId(params.recordId),
    followStatus: params.followUpStatus,
    remark: params.followUpContent || undefined,
  }
}

function buildBatchFollowRequest(params: BatchSaveParams): BatchRequest {
  return {
    changeLogIds: params.recordIds.map((recordId) =>
      normalizeChangeLogId(recordId),
    ),
    followStatus: params.followUpStatus,
    remark: params.followUpContent || undefined,
  }
}

function buildFollowHistoryRequest(
  params: PagingRequest<FollowUpParams>,
): HistoryRequest {
  return {
    condition: {
      changeLogId: normalizeChangeLogId(params.condition?.recordId ?? ''),
    },
  }
}

function buildPreviewClosePreviousBansRequest(
  params: PreviewParams,
): PreviewRequest {
  return {
    condition: {
      changeLogId: normalizeChangeLogId(params.changeLogId),
    },
  }
}

function buildClosePreviousBansRequest(params: CloseParams): CloseRequest {
  return {
    changeLogId: normalizeChangeLogId(params.changeLogId),
    remark: params.remark || undefined,
  }
}

function buildUpdateChangeReasonRequest(params: ContentParams): ContentParams {
  return {
    changeLogId: normalizeChangeLogId(params.changeLogId),
    content: params.content,
  }
}

function buildUpdateCorrectiveActionsRequest(
  params: ContentParams,
): ContentParams {
  return {
    changeLogId: normalizeChangeLogId(params.changeLogId),
    content: params.content,
  }
}

export function buildQueryWeeklyStatsRequest(
  params: PagingRequest<StatsParams>,
): StatsRequest {
  const condition = params.condition ?? {}
  const view = condition.dimension ?? 'agency'
  return {
    condition: {
      accountType:
        view === 'agencyAccountType'
          ? condition.accountType || undefined
          : undefined,
      agency:
        view === 'agencyAccountType'
          ? condition.agency || undefined
          : undefined,
      dateRange: condition.weekRange ?? [],
      platform: condition.mediaPlatform || undefined,
      view,
    },
    orderBy: condition.orderBy?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    sortBy: condition.sortBy ?? 'bannedWeekSpend',
  }
}

function buildWeeklyStatsItemId(
  item: WeeklyItem,
  index: number,
  view?: string,
) {
  const parts = [
    view || 'stats',
    item.agency,
    item.customerId,
    item.customerName,
    item.accountType,
    index,
  ].filter((value) => value !== undefined && value !== null && value !== '')

  return parts.join('-')
}

function mapWeeklyStatsItem(
  item: WeeklyItem,
  index: number,
  response?: WeeklyResponse,
) {
  const naturalWeek =
    response?.startDate && response.endDate
      ? `${response.startDate} ~ ${response.endDate}`
      : undefined

  return {
    accountType: item.accountType,
    agency: item.agency,
    allAccountBlockRate: item.allBanRate,
    blockedSpendShare: item.spendShare,
    blockedWeeklySpend: item.bannedWeekSpend,
    consumedAccountBlockRate: item.spendBanRate,
    consumedAccountCount: item.spendAccounts,
    consumedEndBlockedAccountCount: item.bannedSpendAccounts,
    customerId: item.customerId,
    customerName: item.customerName,
    endBlockedAccountCount: item.bannedEnd,
    id: buildWeeklyStatsItemId(item, index, response?.view),
    naturalWeek,
    totalAccountCount: item.allAccounts,
    weeklySpend: item.totalWeekSpend,
  }
}

export function mapQueryWeeklyStatsResponse(
  response: undefined | WeeklyResponse,
) {
  return {
    items:
      response?.list?.map((item, index) =>
        mapWeeklyStatsItem(item, index, response),
      ) || [],
    summary: response?.summary
      ? mapWeeklyStatsItem(response.summary, 0, response)
      : undefined,
    total: Number(response?.total ?? 0),
  }
}

function mapPreviewClosePreviousBansResponse(response?: PreviewResponse) {
  const targets = response?.targets ?? []
  return {
    changeLogId: Number(response?.changeLogId ?? 0),
    targets: targets.map((item, index) => {
      const changeLogId = Number(item.changeLogId ?? index)
      return {
        accountId: item.accountId,
        accountType: item.accountType,
        changedAt: item.changedAt,
        changeLogId: item.changeLogId,
        currentFollowStatus: item.currentFollowStatus,
        currentFollowUser: item.currentFollowOperator ?? item.followOperator,
        id: String(changeLogId),
        mediaPlatform: item.mediaPlatform,
        yesterdayUsdSpend: item.yesterdaySpendUsd,
      }
    }),
    total: Number(response?.total ?? targets.length),
  }
}

function mapClosePreviousBansResponse(response?: CloseResponse) {
  return {
    affectedCount: Number(response?.affectedCount ?? 0),
  }
}

export const accountStatusMonitorApi = defineApi('operation', ({ prefix }) => {
  /**
   * 监控记录/分页查询。
   * POST /gobestads/operation/adsAccountStatus/queryRecordPage
   */
  function queryMonitorRecords(params: PageParams) {
    return requestClient
      .post<PageResponse>(
        prefix('adsAccountStatus/queryRecordPage'),
        buildQueryRecordPageRequest(params),
      )
      .then(mapQueryRecordPageResponse)
  }

  /**
   * 封停统计/分页查询。
   * POST /gobestads/operation/adsAccountStatus/queryWeeklyStats
   */
  function queryBlockRateStats(params: PagingRequest<StatsParams>) {
    return requestClient
      .post<WeeklyResponse>(
        prefix('adsAccountStatus/queryWeeklyStats'),
        buildQueryWeeklyStatsRequest(params),
      )
      .then(mapQueryWeeklyStatsResponse)
  }

  /**
   * 监控记录跟进历史列表。
   * POST /gobestads/operation/adsAccountStatus/queryFollowHistory
   */
  function queryFollowUpRecords(params: PagingRequest<FollowUpParams>) {
    return requestClient
      .post<HistoryResponse>(
        prefix('adsAccountStatus/queryFollowHistory'),
        buildFollowHistoryRequest(params),
      )
      .then(mapFollowHistoryResponse)
  }

  /**
   * 监控记录跟进。
   * POST /gobestads/operation/adsAccountStatus/follow
   */
  function saveFollowUpRecord(params: SaveParams) {
    return requestClient.post(
      prefix('adsAccountStatus/follow'),
      buildFollowRequest(params),
    )
  }

  /**
   * 监控记录批量跟进。
   * POST /gobestads/operation/adsAccountStatus/batchFollow
   */
  function batchSaveFollowUpRecords(params: BatchSaveParams) {
    return requestClient.post(
      prefix('adsAccountStatus/batchFollow'),
      buildBatchFollowRequest(params),
    )
  }

  /**
   * 监控记录/一键关闭预览。
   * POST /gobestads/operation/adsAccountStatus/previewClosePreviousBans
   */
  function previewClosePreviousBans(params: PreviewParams) {
    return requestClient
      .post<PreviewResponse>(
        prefix('adsAccountStatus/previewClosePreviousBans'),
        buildPreviewClosePreviousBansRequest(params),
      )
      .then(mapPreviewClosePreviousBansResponse)
  }

  /**
   * 监控记录/一键关闭确认。
   * POST /gobestads/operation/adsAccountStatus/closePreviousBans
   */
  function closePreviousBans(params: CloseParams) {
    return requestClient
      .post<CloseResponse>(
        prefix('adsAccountStatus/closePreviousBans'),
        buildClosePreviousBansRequest(params),
      )
      .then(mapClosePreviousBansResponse)
  }

  /**
   * 编辑监控记录封禁原因。
   * POST /gobestads/operation/adsAccountStatus/updateChangeReason
   */
  function updateChangeReason(params: ContentParams) {
    return requestClient.post(
      prefix('adsAccountStatus/updateChangeReason'),
      buildUpdateChangeReasonRequest(params),
    )
  }

  /**
   * 编辑监控记录整改建议。
   * POST /gobestads/operation/adsAccountStatus/updateCorrectiveActions
   */
  function updateCorrectiveActions(params: ContentParams) {
    return requestClient.post(
      prefix('adsAccountStatus/updateCorrectiveActions'),
      buildUpdateCorrectiveActionsRequest(params),
    )
  }

  /**
   * 监控记录导出，复用列表分页查询参数。
   * POST /gobestads/operation/export/submit
   */
  function exportMonitorRecords(params: PageParams) {
    const requestParams = buildQueryRecordPageRequest(params)
    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_ACCOUNT_STATUS_RECORD_EXPORT,
      requestParams,
      {
        orderBy: requestParams.orderBy,
        sortBy: requestParams.sortBy,
      },
    )
  }

  return {
    batchSaveFollowUpRecords,
    closePreviousBans,
    exportMonitorRecords,
    previewClosePreviousBans,
    queryBlockRateStats,
    queryFollowUpRecords,
    queryMonitorRecords,
    saveFollowUpRecord,
    updateChangeReason,
    updateCorrectiveActions,
  }
})
